import React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { Drawer, Avatar, Badge, Button, Divider, message } from 'antd';
import {
  CloseOutlined,
  PlusCircleOutlined,
  UserOutlined,
  TrophyOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  AimOutlined,
  ThunderboltOutlined,
  ReadOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useInstallApp } from '../../lib/installApp';
import { signOutWithPush } from '../../lib/webPush';
import { useAppSelector } from '../../store/hooks';
import {
  selectHasOffer,
  selectHasPartnerInvite,
  selectUser,
  selectUserData,
} from '../../store/slices/userSlice';
import { GUEST_MENU, MEMBER_MENU } from '../../shared/variables/menus';
import {
  ROUTE_LOGIN,
  ROUTE_PLAY,
  ROUTE_OPENING_REQUEST,
} from '../../shared/variables/routes';
import { storageImage } from '../../shared/utils/storage-image';
import { useCongratsBadge } from '../../lib/congratsBadge';

type Props = {
  open: boolean;
  onClose: () => void;
  onClosed: () => void;
};

export default function MobileMenuDrawer({ open, onClose, onClosed }: Props) {
  const router = useRouter();
  const user = useAppSelector(selectUser);
  const userData = useAppSelector(selectUserData);
  const hasOffer = useAppSelector(selectHasOffer);
  const hasPartnerInvite = useAppSelector(selectHasPartnerInvite);
  const hasIndicator = hasOffer || hasPartnerInvite;
  const newCongrats = useCongratsBadge(userData?.id);
  const { t } = useTranslation();
  const installApp = useInstallApp();
  const canInstall = installApp.path !== 'installed' && installApp.path !== 'unsupported';

  const NAVIGATION = [
    { text: t('mobileMenu.leagues'), url: '/ligler', icon: <TrophyOutlined /> },
    { text: t('mobileMenu.clubs'), url: '/kulupler', icon: <EnvironmentOutlined /> },
    { text: t('mobileMenu.courts'), url: '/kortlar', icon: <AimOutlined /> },
    { text: t('mobileMenu.tournaments'), url: '/turnuvalar', icon: <FlagOutlined /> },
    { text: t('mobileMenu.training'), url: '/antrenmanlar', icon: <ThunderboltOutlined /> },
    { text: t('mobileMenu.tennisGuide'), url: '/tenis', icon: <ReadOutlined /> },
  ];

  const SECONDARY = [
    { text: t('topMenu.addLeague'), url: ROUTE_OPENING_REQUEST },
    { text: t('topMenu.pricing'), url: '/ucretlendirme' },
    { text: t('topMenu.howToJoin'), url: '/akademi/nasil-uye-olurum' },
    { text: t('topMenu.matchPlaying'), url: '/akademi/mac-oynama-sureci' },
    { text: t('topMenu.prizes'), url: '/oduller' },
    { text: t('topMenu.help'), url: '/yardim' },
  ];

  const accountMenu = user ? MEMBER_MENU : GUEST_MENU;

  const go = (url: string, external = false) => {
    onClose();
    if (external) {
      window.open(url, '_blank');
    } else {
      router.push(url);
    }
  };

  const handleAccountClick = async (
    e: React.MouseEvent,
    clickAction?: string,
  ) => {
    if (clickAction === 'signOut') {
      e.preventDefault();
      onClose();
      try { await signOutWithPush(); }
      catch { message.error(t('errors.pushLogoutFailed')); return; }
      await router.push(ROUTE_LOGIN);
      return;
    }
    onClose();
  };

  return (
    <Drawer
      placement="right"
      onClose={onClose}
      afterVisibleChange={visible => { if (!visible) onClosed(); }}
      visible={open}
      closable={false}
      width="min(360px, calc(100vw - 24px))"
      className="mobile-menu-drawer"
      title={t('mobile.navigation')}
      headerStyle={{ display: 'none' }}
      bodyStyle={{ padding: 0 }}
    >
      <div className="mobile-drawer" id="mobile-menu">
        <div className="mobile-drawer-header">
          <a className="mobile-drawer-user" href={user ? '/profilim' : ROUTE_LOGIN} onClick={onClose}>
            <Badge dot={hasIndicator}>
              <Avatar
                size={44}
                src={storageImage(userData?.foto_mini_url, 44)}
                icon={<UserOutlined />}
              />
            </Badge>
            <div className="mobile-drawer-user-info">
              {user ? (
                <>
                  <strong>
                    {userData ? `${userData?.ad} ${userData?.soyad}` : t('header.account')}
                  </strong>
                  <small>{t('cta.goToProfile')}</small>
                </>
              ) : (
                <>
                  <strong>{t('header.loginCta')}</strong>
                  <small>{t('header.loginCtaSub')}</small>
                </>
              )}
            </div>
          </a>
          <button type="button" className="mobile-drawer-close" onClick={onClose} aria-label={t('common.close')}>
            <CloseOutlined />
          </button>
        </div>

        {user && newCongrats > 0 && (
          // Ç7: yeni tebrikler ana sayfa akışında ("Son ziyaretinden beri N yeni tebrik").
          <Link href="/">
            <a href="/" className="mobile-drawer-congrats" onClick={() => onClose()}>
              <span aria-hidden="true">👏</span>
              <span>{t('header.newCongrats', { count: newCongrats })}</span>
            </a>
          </Link>
        )}

        {user && (
          <div className="mobile-drawer-cta">
            <Button
              type="primary"
              block
              icon={<PlusCircleOutlined />}
              onClick={() => go(ROUTE_PLAY)}
            >
              {t('cta.proposeMatch')}
            </Button>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        <nav className="mobile-drawer-nav">
          {NAVIGATION.map(item => (
            <Link href={item.url} key={item.url}>
              <a
                href={item.url}
                aria-current={router.pathname.startsWith(item.url) ? 'page' : undefined}
                className={`mobile-drawer-link ${
                  router.pathname.startsWith(item.url) ? 'is-active' : ''
                }`}
                onClick={() => onClose()}
              >
                <span className="mobile-drawer-link-icon">{item.icon}</span>
                <span>{item.text}</span>
              </a>
            </Link>
          ))}
        </nav>

        <Divider style={{ margin: '8px 0' }} />

        <nav className="mobile-drawer-nav mobile-drawer-nav-secondary">
          {accountMenu
            .filter(i => i.active)
            .map(item => (
              <a
                key={item.text}
                href={item.url}
                className="mobile-drawer-link"
                onClick={e => handleAccountClick(e, (item as any).clickAction)}
              >
                <Badge dot={item.showDot && hasIndicator} offset={[6, 0]}>
                  <span>{t(item.text)}</span>
                </Badge>
              </a>
            ))}
        </nav>

        {canInstall && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <button
              type="button"
              className="mobile-drawer-link mobile-drawer-install"
              onClick={() => { onClose(); installApp.install(); }}
            >
              <span className="mobile-drawer-link-icon"><MobileOutlined aria-hidden="true" /></span>
              <span>{t('installApp.menuItem')}</span>
            </button>
          </>
        )}

        <Divider style={{ margin: '8px 0' }} />

        <nav className="mobile-drawer-nav mobile-drawer-nav-secondary">
          {SECONDARY.map(item => (
            <a
              key={item.url}
              href={item.url}
              className="mobile-drawer-link"
              onClick={onClose}
            >
              {item.text}
            </a>
          ))}
        </nav>
      </div>
    </Drawer>
  );
}
